<template>
  <section class="partners-section">
    <div class="partners-inner">
      <h3 class="partners-title">Our Partners</h3>

      <div
        class="marquee"
        @mouseenter="paused = true"
        @mouseleave="paused = false"
      >
        <div class="track" :class="{ paused }">
          <div
            v-for="(partner, i) in partnersDup"
            :key="'p-' + i"
            class="logo-item"
            @mouseenter="activePartner = partner"
            @mouseleave="activePartner = null"
          >
            <img :src="partner.logo" :alt="partner.name || 'Partner'" />
            <!-- Hover overlay -->
            <div v-if="activePartner === partner && (partner.name || partner.website)" class="logo-overlay">
              <p v-if="partner.name" class="overlay-name">{{ partner.name }}</p>
              <p v-if="partner.description" class="overlay-desc">{{ partner.description }}</p>
              <a
                v-if="partner.website"
                :href="partner.website"
                target="_blank"
                rel="noopener"
                class="overlay-link"
              >
                Visit Website →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import api from '@/services/api';
import { mediaUrl } from '@/services/media';

export default {
  name: 'PartnersLogos',
  data() {
    const fallback = [
      require('../assets/001.webp'),
      require('../assets/002.webp'),
      require('../assets/003.webp'),
      require('../assets/004.webp'),
      require('../assets/005.webp'),
      require('../assets/006.webp'),
      require('../assets/007.webp'),
      require('../assets/008.webp'),
      require('../assets/009.webp'),
    ].map(src => ({ logo: src, name: '', website: '', description: '' }));
    return {
      partners: fallback,
      partnersDup: fallback.concat(fallback),
      paused: false,
      activePartner: null,
    }
  },
  async created() {
    try {
      const data = await api.getPartners();
      const list = api.extractList(data);
      if (list.length > 0) {
        const mapped = list.map(p => ({
          logo: mediaUrl(p.logo || p.image || ''),
          name: p.name || '',
          website: p.website || p.url || p.link || '',
          description: p.description || '',
        }));
        this.partners = mapped;
        this.partnersDup = mapped.concat(mapped);
      }
    } catch (e) {
      console.warn('[Logos] API failed, using fallback:', e.message);
    }
  }
}
</script>

<style scoped>
* { box-sizing: border-box; }

.partners-section {
  width: 100%;
  padding: 3rem 1.5rem;
  background: radial-gradient(circle at top left, #fff7f8 0, #ffffff 40%, #fffbe6 100%);
}
.partners-inner { max-width: 1920px; margin: 0 auto; display: flex; flex-direction: column; align-items: center; }
.partners-title { font-size: clamp(1.5rem, 4vw, 2.75rem); font-weight: 900; color: #2c3e50; margin-bottom: 1.75rem; text-align: center; }

.marquee { position: relative; overflow: hidden; width: 100%; display: flex; justify-content: center; }

.track {
  display: flex;
  align-items: center;
  gap: 3.5rem;
  will-change: transform;
  animation: marquee 25s linear infinite;
}
.track.paused { animation-play-state: paused; }

.logo-item {
  flex: 0 0 auto;
  width: 220px;
  max-width: 26vw;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  transition: transform 0.25s ease;
}
.logo-item:hover { transform: scale(1.08); }

.logo-item img {
  max-width: 92%;
  max-height: 92%;
  object-fit: contain;
  filter: grayscale(0.02);
  opacity: 1;
  transition: filter 0.3s, opacity 0.3s;
}
.logo-item:hover img { filter: grayscale(0); opacity: 1; }

/* ── Hover overlay ── */
.logo-overlay {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  border-radius: 10px;
  padding: 12px 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  border: 1px solid #eee;
  min-width: 180px;
  max-width: 260px;
  z-index: 100;
  text-align: center;
  animation: fadeIn 0.2s ease;
}
.logo-overlay::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 8px solid transparent;
  border-top-color: #fff;
}

@keyframes fadeIn { from { opacity: 0; transform: translateX(-50%) translateY(4px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }

.overlay-name { font-size: 14px; font-weight: 700; color: #1a1a1a; margin: 0 0 4px; }
.overlay-desc { font-size: 12px; color: #777; margin: 0 0 8px; line-height: 1.4; }
.overlay-link {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  color: #C02130;
  text-decoration: none;
  padding: 4px 12px;
  border: 1px solid #C02130;
  border-radius: 16px;
  transition: all 0.2s;
}
.overlay-link:hover { background: #C02130; color: #fff; }

@keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }

@media (min-width: 1440px) { .partners-section { padding: 4rem 4rem; } .track { gap: 4.5rem; } .logo-item { width: 260px; height: 140px; max-width: none; } }
@media (max-width: 1024px) { .track { gap: 2.5rem; } .logo-item { width: 180px; height: 100px; max-width: 32vw; } }
@media (max-width: 768px) { .track { gap: 1.75rem; } .logo-item { width: 150px; height: 80px; max-width: 40vw; } .logo-overlay { display: none; } }
@media (max-width: 480px) { .track { gap: 1.25rem; } .logo-item { width: 120px; height: 70px; max-width: 48vw; } }
</style>
